

import { FC, PropsWithChildren, ReactNode, useId } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Container } from '@eneco/custom-components/flows/PriceDetails';
import { useFormatter } from '@eneco/i18n';
import { Box, Expandable, Divider, Ribbon, Stack, Text } from '@eneco/sparky';
import { ElectricityIcon, GasIcon, WarmthIcon, WaterIcon, GiftIcon } from '@eneco/sparky/icons';
import { BackgroundColoredColors, TextColors } from '@eneco/sparky/types';

import { DetailsType } from '../../types';

type Details = {
  text?: string;
  label?: string;
  value?: string | number;
  highlightText?: boolean;
  highlightValue?: boolean;
  highlightDivider?: boolean;
};
interface FooterDetails extends Omit<Details, 'text'> {
  text?: string;
}

type DetailCustom = {
  text: string;
  value: string | number;
  sign?: string;
};
type FooterDetailCustom = { label?: string; text?: string; value: string | number; sign?: string };

interface CompoundPriceDetailsProps {
  Title: typeof Title;

  Content: typeof Content;
  Footer: typeof Footer;

  ContentCustom: typeof ContentCustom;
  FooterCustom: typeof FooterCustom;
}

type TitleProps =
  | {
      type: DetailsType;
      backgroundColor?: null;
      textColor?: null;
      icon?: null;
      children: string;
    }
  | {
      type?: null;
      backgroundColor: keyof BackgroundColoredColors;
      textColor: keyof TextColors;
      icon?: ReactNode;
      children: string;
    };

interface ContentProps {
  summary: Details;
  details?: Details[];
  text?: string;
}

interface RowProps extends Omit<Details, 'text'> {
  text?: string;
  id?: string;
  isNested?: boolean;
}

interface FooterProps {
  summary: FooterDetails;
  details?: Details[];
}

interface ContentCustomProps {
  summary: DetailCustom;
  details?: DetailCustom[];
  noFractionDigits?: boolean;
}
interface FooterCustomProps {
  summary: FooterDetailCustom;
  noFractionDigits?: boolean;
}

const typeTheme: Record<
  DetailsType,
  { backgroundColor: keyof BackgroundColoredColors; textColor: keyof TextColors; icon?: ReactNode }
> = {
  [DetailsType.Gas]: {
    backgroundColor: 'backgroundVarOne',
    textColor: 'textOnBackgroundVarOne',
    icon: <GasIcon color="iconGas" />,
  },
  [DetailsType.Water]: {
    backgroundColor: 'backgroundVarThree',
    textColor: 'textOnBackgroundVarThree',
    icon: <WaterIcon color="iconCooling" />,
  },
  [DetailsType.Warmth]: {
    backgroundColor: 'backgroundVarTwo',
    textColor: 'textOnBackgroundVarTwo',
    icon: <WarmthIcon color="iconHeat" />,
  },
  [DetailsType.Redelivery]: {
    backgroundColor: 'backgroundVarFive',
    textColor: 'textOnBackgroundVarFive',
    icon: <ElectricityIcon color="iconElectricity" />,
  },
  [DetailsType.Electricity]: {
    backgroundColor: 'backgroundVarFive',
    textColor: 'textOnBackgroundVarFive',
    icon: <ElectricityIcon color="iconElectricity" />,
  },
  [DetailsType.Cashback]: {
    backgroundColor: 'backgroundVarOne',
    textColor: 'textOnBackgroundVarOne',
    icon: <GiftIcon color="iconOnBackgroundVarOne" />,
  },
};

const Title: FC<TitleProps> = ({ type, backgroundColor, textColor, icon, children }) => {
  let theme = typeTheme[DetailsType.Electricity];

  if (type) {
    theme = typeTheme[type];
  } else if (backgroundColor && textColor) {
    theme = { backgroundColor, textColor, icon };
  }

  return type === DetailsType.Cashback ? (
    <Ribbon>
      <Box paddingX="3" paddingY="1">
        <Stack direction="row" alignY="center" gap="1">
          <GiftIcon color="iconInverted" />,
          <Text weight="bold" size="BodyM" color="textInverted">
            {children}
          </Text>
        </Stack>
      </Box>
    </Ribbon>
  ) : (
    <Box backgroundColor={theme.backgroundColor} padding="3">
      <Stack direction="row" alignY="center" gap="1">
        {theme.icon}
        <Text weight="bold" color={theme.textColor}>
          {children}
        </Text>
      </Stack>
    </Box>
  );
};
const Row: FC<RowProps> = ({ text, label, id, value, highlightText, highlightValue, highlightDivider, isNested }) => {
  const { currency } = useFormatter();

  return (
    <>
      {isNested && <Divider isDecorative={true} emphasis={highlightDivider ? 'high' : 'low'} />}

      <Box paddingY="3" paddingLeft="3" paddingRight={isNested ? '3' : '0'}>
        <Text size={isNested ? 'BodyS' : 'BodyM'}>
          <Stack direction="row" alignY="start" gap="4">
            {(text || label) && (
              <Stack.Item grow={typeof value !== 'string'}>
                {isNested ? (
                  <Text weight={highlightText ? 'bold' : 'regular'}>
                    <RichText html={text?.replace('€ ', '€&nbsp;')} />
                  </Text>
                ) : (
                  <Text id={id}>
                    {label && <Text weight="bold">{label} </Text>}
                    <Text weight={highlightText ? 'bold' : 'regular'}>
                      <RichText html={text} />
                    </Text>
                  </Text>
                )}
              </Stack.Item>
            )}

            {(value === 0 || value) && (
              <Stack.Item shrink={false} grow={typeof value === 'string'}>
                <Text weight={highlightValue ? 'bold' : 'regular'} align="right">
                  {typeof value === 'string' ? value : currency.euro(value)}
                </Text>
              </Stack.Item>
            )}
          </Stack>
        </Text>
      </Box>
    </>
  );
};
const Content: FC<ContentProps> = ({ summary, details, text }) => {
  const id = `${useId()}-${summary?.text?.replaceAll(' ', '-')}`;

  return !details && !text ? (
    <Box paddingRight="10">
      <Box paddingRight="1">
        <Row {...summary} />
      </Box>
    </Box>
  ) : (
    <Expandable>
      <Box paddingRight="2">
        <Expandable.Trigger isTransparent aria-labelledby={id}>
          <Row id={id} {...summary} />
        </Expandable.Trigger>
      </Box>

      <Expandable.Content>
        {details?.map((detail, index) => (
          <Box key={index} backgroundColor="backgroundSecondary">
            <Row isNested {...detail} />
          </Box>
        ))}

        {text && (
          <Box padding="3" backgroundColor="backgroundSecondary">
            <RichText html={text} />
          </Box>
        )}
      </Expandable.Content>
    </Expandable>
  );
};
const Footer: FC<FooterProps> = ({ summary, details }) => (
  <Box backgroundColor="backgroundSecondary">
    {details?.map((detail, index) => (
      <Box key={index}>
        <Row isNested {...detail} />
      </Box>
    ))}

    <Divider isDecorative={true} emphasis="high" />
    <Box paddingRight="10">
      <Box paddingRight="1">
        <Row {...summary} highlightValue />
      </Box>
    </Box>
  </Box>
);

const ContentCustom: FC<ContentCustomProps> = ({ summary: { text, value, sign }, details, noFractionDigits }) => {
  const id = `${useId()}-${text.replaceAll(' ', '-')}`;

  const { currency } = useFormatter();

  const formatCurrency = (value: string | number = 0) =>
    noFractionDigits ? currency.euroNoFractionDigits(value) : currency.euro(value);

  if (!details?.length) {
    return (
      <Box paddingY="3" paddingLeft="3" paddingRight="10">
        <Stack direction="row" alignY="start" gap="2">
          <Stack.Item grow={true}>
            <Text id={id}>{text}</Text>
          </Stack.Item>
          <Stack.Item shrink={false}>
            <Text>{sign ? `${sign} ${formatCurrency(value)}` : formatCurrency(value)}</Text>
          </Stack.Item>
        </Stack>
      </Box>
    );
  }

  return (
    <Expandable>
      <Box paddingRight="3">
        <Expandable.Trigger isTransparent aria-labelledby={id}>
          <Box paddingY="3" paddingLeft="3" paddingRight="1">
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Text id={id}>{text}</Text>
              </Stack.Item>
              <Stack.Item shrink={false}>
                <Text>{sign ? `${sign} ${formatCurrency(value)}` : formatCurrency(value)}</Text>
              </Stack.Item>
            </Stack>
          </Box>
        </Expandable.Trigger>
      </Box>

      <Expandable.Content>
        {details.map(({ text, value }, index) => (
          <Box backgroundColor="backgroundSecondary" key={`${index}${value}`}>
            <Divider />

            <Box paddingY="3" paddingLeft="3" paddingRight="10">
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Text size="BodyS">
                    <RichText html={text.replace('€ ', '€&nbsp;')} />
                  </Text>
                </Stack.Item>
                <Stack.Item shrink={false}>
                  <Text size="BodyS">
                    <RichText
                      html={
                        sign
                          ? `${sign} ${formatCurrency(value).replace('€ ', '€&nbsp;')}`
                          : formatCurrency(value).replace('€ ', '€&nbsp;')
                      }
                    />
                  </Text>
                </Stack.Item>
              </Stack>
            </Box>
          </Box>
        ))}
      </Expandable.Content>
    </Expandable>
  );
};
const FooterCustom: FC<FooterCustomProps> = ({
  summary: { text, value, sign, label = 'Totaal' },
  noFractionDigits,
}) => {
  const { currency } = useFormatter();

  const formatCurrency = (value: string | number) =>
    noFractionDigits ? currency.euroNoFractionDigits(value) : currency.euro(value);

  const getDisplayValue = () => {
    if (typeof value === 'string') return value;

    const formatted = formatCurrency(value).replace('€ ', '€&nbsp;');

    return sign ? `${sign} ${formatted}` : formatted;
  };

  return (
    <Box backgroundColor="backgroundSecondary">
      <Divider isDecorative={true} emphasis="high" />

      <Box paddingY="3" paddingLeft="3" paddingRight="10">
        <Stack direction="row" alignY="start" gap="2">
          <Stack.Item grow={true}>
            <RichText html={`<strong>${label}</strong>`} />
            {text && ` (${text})`}
          </Stack.Item>

          <Stack.Item shrink={false}>
            <Text weight="bold">{getDisplayValue()}</Text>
          </Stack.Item>
        </Stack>
      </Box>
    </Box>
  );
};

export const PriceDetails: FC<PropsWithChildren<unknown>> & CompoundPriceDetailsProps = ({ children }) => (
  <Container borderRadius="m" backgroundColor="backgroundPrimary" overflow="hidden">
    {children}
  </Container>
);

PriceDetails.Title = Title;
PriceDetails.Content = Content;
PriceDetails.Footer = Footer;

PriceDetails.ContentCustom = ContentCustom;
PriceDetails.FooterCustom = FooterCustom;
